import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { App, Descriptions, Image, Modal, Popconfirm, Select } from 'antd';
import { useLocation } from 'react-router-dom';
import { listIssues, updateIssue, deleteIssue } from '../../api/issues';
import { getApiErrorMessage } from '../../utils/apiError';
import TableCard from '../../components/TableCard';
import { SelectWrap, SearchBar } from '../../components/ToolbarControls';
import { StatusTag, ViewIcon, DeleteIcon } from '../../components/UIKit';
import { isPageVisible } from '../../utils/pageVisibility';

const PlayIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/><polygon points="10 8 16 12 10 16 10 8"/>
  </svg>
);

export default function Issues() {
  const { message, notification } = App.useApp();
  const location = useLocation();
  const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:4000';
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tableSearch, setTableSearch] = useState('');
  const [filters, setFilters] = useState(() => {
    const sp = new URLSearchParams(window.location.search);
    return { statut: sp.get('statut') || '' };
  });
  const [viewOpen, setViewOpen] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [viewStatut, setViewStatut] = useState('OPEN');
  const [viewSaving, setViewSaving] = useState(false);

  const fetchAll = useCallback(async (opts = {}) => {
    const silent = opts.silent === true;
    if (!silent) setLoading(true);
    try {
      const params = {};
      if (filters.statut) params.statut = filters.statut;
      const data = await listIssues(params);
      setRows(data);
    } catch (e) {
      if (!silent) {
        notification.error({ message: getApiErrorMessage(e, 'Impossible de charger les signalements.'), placement: 'topRight' });
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, [filters.statut, notification]);

  useEffect(() => {
    fetchAll();
    const t = setInterval(() => {
      if (isPageVisible()) fetchAll({ silent: true });
    }, 10000);
    const onVis = () => {
      if (isPageVisible()) fetchAll({ silent: true });
    };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      clearInterval(t);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [fetchAll]);

  useEffect(() => {
    const onRefresh = () => {
      fetchAll();
    };
    window.addEventListener('issues:refresh', onRefresh);
    return () => window.removeEventListener('issues:refresh', onRefresh);
  }, [fetchAll]);

  useEffect(() => {
    const sp = new URLSearchParams(location.search);
    const nextStatut = sp.get('statut') || '';
    setFilters((f) => (f?.statut === nextStatut ? f : { ...f, statut: nextStatut }));
  }, [location.search]);

  const onView = useCallback((record) => { setViewing(record); setViewStatut(record?.statut || 'OPEN'); setViewOpen(true); }, []);
  const onDeleteRow = useCallback(async (record) => {
    try { await deleteIssue(record.id); message.success('Signalement supprimé.'); fetchAll(); }
    catch (e) { notification.error({ message: getApiErrorMessage(e, 'Suppression échouée.'), placement: 'topRight' }); }
  }, [fetchAll, message, notification]);

  const onSaveViewStatut = useCallback(async () => {
    if (!viewing?.id) return;
    setViewSaving(true);
    try {
      await updateIssue(viewing.id, { statut: viewStatut });
      message.success('Statut mis à jour.');
      setViewing((prev) => prev ? { ...prev, statut: viewStatut } : prev);
      fetchAll();
    } catch (e) { notification.error({ message: getApiErrorMessage(e, 'Mise à jour échouée.'), placement: 'topRight' }); }
    finally { setViewSaving(false); }
  }, [fetchAll, message, notification, viewStatut, viewing?.id]);

  const onQuickInProgress = useCallback(async (record) => {
    try { await updateIssue(record.id, { statut: 'IN_PROGRESS' }); message.success('Passé en cours.'); fetchAll(); }
    catch (e) { notification.error({ message: getApiErrorMessage(e, 'Mise à jour échouée.'), placement: 'topRight' }); }
  }, [fetchAll, message, notification]);

  const filteredRows = useMemo(() => {
    const q = String(tableSearch || '').trim().toLowerCase();
    const base = Array.isArray(rows) ? rows : [];
    if (!q) return base;
    return base.filter((r) => {
      const code = String(r?.Student?.student_code || '').toLowerCase();
      const prenom = String(r?.Student?.User?.prenom || '').toLowerCase();
      const nom = String(r?.Student?.User?.nom || '').toLowerCase();
      return code.includes(q) || prenom.includes(q) || nom.includes(q);
    });
  }, [rows, tableSearch]);

  return (
    <div className="page">
      <TableCard
        label="Signalements"
        loading={loading}
        rows={filteredRows}
        toolbar={<>
          <SelectWrap
            value={filters.statut}
            onChange={(v) => setFilters((f) => ({ ...f, statut: v }))}
            options={[{ value: '', label: 'Tous' }, { value: 'OPEN', label: 'En attente' }, { value: 'IN_PROGRESS', label: 'En cours' }, { value: 'RESOLVED', label: 'Résolu' }]}
          />
          <button className="btn-outline" onClick={fetchAll}>Rafraîchir</button>
        </>}
        search={<SearchBar value={tableSearch} onChange={setTableSearch} placeholder="Rechercher un étudiant (ID, nom)" />}
      >
        {(pageRows) => (
        <table>
          <thead>
            <tr>
              <th>ID étudiant</th>
              <th>Titre</th>
              <th>Étudiant</th>
              <th>Chambre</th>
              <th>Résidence</th>
              <th>Envoyé le</th>
              <th>Résolu le</th>
              <th>Statut</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {pageRows.map((r) => {
              return (
                <tr key={r.id}>
                  <td>{r.Student?.student_code || '-'}</td>
                  <td>{r.titre || '-'}</td>
                  <td>{`${r.Student?.User?.prenom || ''} ${r.Student?.User?.nom || ''}`.trim() || '-'}</td>
                  <td>{r.Student?.RoomAssignments?.[0]?.Room?.code || '-'}</td>
                  <td>{r.Student?.Residence?.nom || '-'}</td>
                  <td>{r.createdAt ? new Date(r.createdAt).toLocaleString() : '-'}</td>
                  <td>{r.statut === 'RESOLVED' && r.updatedAt ? new Date(r.updatedAt).toLocaleString() : '-'}</td>
                  <td><StatusTag status={r.statut} /></td>
                  <td>
                    <div className="actions-cell">
                      <button className="action-btn" title="Voir" onClick={() => onView(r)}><ViewIcon /></button>
                      {r?.statut === 'OPEN' && <button className="action-btn" title="Passer en cours" onClick={() => onQuickInProgress(r)}><PlayIcon /></button>}
                      <Popconfirm title="Supprimer ce signalement ?" description={r.titre} okText="Supprimer" cancelText="Annuler" okType="danger" onConfirm={() => onDeleteRow(r)}>
                        <button className="action-btn danger" title="Supprimer"><DeleteIcon /></button>
                      </Popconfirm>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        )}
      </TableCard>

      {/* View Modal */}
      <Modal
        title={viewing ? `Signalement #${viewing.id}` : 'Signalement'}
        open={viewOpen}
        onCancel={() => { setViewOpen(false); setViewing(null); }}
        width={900}
        footer={
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button className="btn-outline" onClick={() => { setViewOpen(false); setViewing(null); }}>Fermer</button>
            <button className="btn-primary" disabled={viewSaving} onClick={onSaveViewStatut}>{viewSaving ? 'Enregistrement...' : 'Enregistrer'}</button>
          </div>
        }
      >
        {viewing && (() => {
          const attachments = Array.isArray(viewing?.IssueReportAttachments) ? viewing.IssueReportAttachments : [];
          const images = attachments.filter((a) => String(a?.mime_type || '').startsWith('image/'));
          const videos = attachments.filter((a) => String(a?.mime_type || '').startsWith('video/'));
          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <Descriptions bordered size="small" column={2}>
                <Descriptions.Item label="ID étudiant">{viewing?.Student?.student_code || '-'}</Descriptions.Item>
                <Descriptions.Item label="Étudiant">{`${viewing.Student?.User?.prenom || ''} ${viewing.Student?.User?.nom || ''}`.trim() || '-'}</Descriptions.Item>
                <Descriptions.Item label="Email">{viewing.Student?.User?.email || '-'}</Descriptions.Item>
                <Descriptions.Item label="Résidence">{viewing?.Student?.Residence?.nom || '-'}</Descriptions.Item>
                <Descriptions.Item label="Chambre">{viewing.Student?.RoomAssignments?.[0]?.Room?.code || '-'}</Descriptions.Item>
                <Descriptions.Item label="Statut">
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <StatusTag status={viewStatut} />
                    <Select style={{ width: 180 }} value={viewStatut} onChange={setViewStatut} options={[{ value: 'OPEN', label: 'En attente' }, { value: 'IN_PROGRESS', label: 'En cours' }, { value: 'RESOLVED', label: 'Résolu' }]} />
                  </div>
                </Descriptions.Item>
                <Descriptions.Item label="Envoyé le">{viewing?.createdAt ? new Date(viewing.createdAt).toLocaleString() : '-'}</Descriptions.Item>
                <Descriptions.Item label="Résolu le">{viewing?.statut === 'RESOLVED' && viewing?.updatedAt ? new Date(viewing.updatedAt).toLocaleString() : '-'}</Descriptions.Item>
              </Descriptions>
              <hr style={{ margin: '2px 0', border: 'none', borderTop: '0.5px solid rgba(0,0,0,0.08)' }} />
              <div><strong style={{ fontSize: 12 }}>Titre</strong><p style={{ marginTop: 4, fontSize: 12 }}>{viewing.titre || '-'}</p></div>
              <div><strong style={{ fontSize: 12 }}>Description</strong><p style={{ marginTop: 4, fontSize: 12, whiteSpace: 'pre-wrap' }}>{viewing.description || '-'}</p></div>
              {(images.length > 0 || videos.length > 0) && (
                <>
                  <hr style={{ margin: '2px 0', border: 'none', borderTop: '0.5px solid rgba(0,0,0,0.08)' }} />
                  <div>
                    <strong style={{ fontSize: 12 }}>Médias</strong>
                    {images.length > 0 && (
                      <Image.PreviewGroup>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 10, marginTop: 8 }}>
                          {images.map((a) => { const token = localStorage.getItem('access') || ''; const url = a?.path ? `${API_BASE}${a.path}?token=${token}` : null; return url ? <Image key={a.id} src={url} width="100%" style={{ height: 120, objectFit: 'cover', borderRadius: 8 }} /> : null; })}
                        </div>
                      </Image.PreviewGroup>
                    )}
                    {videos.length > 0 && (
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 10, marginTop: 8 }}>
                        {videos.map((a) => { const token = localStorage.getItem('access') || ''; const url = a?.path ? `${API_BASE}${a.path}?token=${token}` : null; return url ? <video key={a.id} src={url} controls style={{ width: '100%', borderRadius: 8 }} /> : null; })}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          );
        })()}
      </Modal>
    </div>
  );
}
